/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, warp-drive/no-legacy-request-patterns */
import Person from '@linc-technologies/linced-common/models/person';
import { computed } from '@ember/object';
import { dependentKeyCompat } from '@ember/object/compat';
import { filterBy, alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';
import { isEmpty } from '@ember/utils';
import type { Type } from '@warp-drive/core/types/symbols';
import { attr, hasMany, type AsyncHasMany } from '@warp-drive/legacy/model';
import type { FragmentArray } from 'ember-data-model-fragments/array/fragment';
import { stripDiacritics } from 'ember-power-select/utils/group-utils';
import Atsi from '@linc-technologies/linced-common/enums/au-atsi';
import DataType from '@linc-technologies/linced-common/enums/data-type';
import GroupType from '@linc-technologies/linced-common/enums/group-type';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import MembershipStatus from '@linc-technologies/linced-common/enums/membership-status';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import FieldAccess from '@linc-technologies/linced-common/enums/roles/field-access';
import Staff from '@linc-technologies/linced-common/enums/roles/staff';
import StaffPageAccess from '@linc-technologies/linced-common/enums/roles/staff-page-access';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import type User from '@linc-technologies/linced-common/models/user';
import type SessionService from '@linc-technologies/linced-common/services/session';
import * as arrays from '@linc-technologies/linced-common/utils/arrays';
import { dynamicField } from '@linc-technologies/linced-common/utils/dynamic-fields';
import moment from 'moment';
import EnrolmentStatus from 'linced-app/enums/enrolment-status';
import MedicalConditionType from 'linced-app/enums/medical-condition-type';
import type Group from 'linced-app/models/group';
import type Medication from 'linced-app/models/medical/medication';
import type Learner from 'linced-app/models/people/learner';
import type MedicalCondition from 'linced-app/models/people/medical-condition';
import type School from 'linced-app/models/school';

// Proto: people.Person.
export default class _Person extends Person {
  declare [Type]: 'person';

  @service
  declare session: SessionService;

  // Technically a belongsTo relationship, but we don't want to auto-fetch user records.
  @attr('string', { defaultValue: '' })
  declare userId: string;

  // Hero ID is a unique numeric identifier, largely for third-party systems which can't cope with UUIDs.
  @attr('number', { defaultValue: 0 })
  declare heroId: number;

  // auditReason provides a reason as to why specific fields changed.
  // This doesn't exist on the back end.  It's used for UPDATE requests.
  @attr('string')
  declare auditReason: string | undefined;

  // The current-year homeroom found by loadHomeroom(): null when the
  // lookup found none, undefined before it has run.
  @tracked _loadedHomeroom: Group | null | undefined;

  // In flight loadHomeroom() lookup, so concurrent callers share one query.
  _homeroomLookup: Promise<Group | null> | undefined;

  // homeroom is the learner's current-year homeroom from the store. It never
  // queries: this app preloads Groups, and loadHomeroom() fetches the
  // transfer case below where a screen needs it.
  @computed('type', 'learner.{homeroomId,enrolmentStub}', '_loadedHomeroom')
  get homeroom(): Group | null {
    if (this.type !== PersonType.LEARNER) {
      return null;
    }

    const currentYear = moment().year();
    const homeroomId = this.get('learner.homeroomId') as string;

    if (!isEmpty(homeroomId)) {
      const group = this.store.peekRecord<Group>('group', homeroomId);
      if (group && group.get('year') === currentYear) {
        return group;
      }
    }

    // Fallback for transfer pre-enrolled students: homeroomId still points at the
    // old school's group until PRE_ENROLLED→CURRENT fires. Look for the
    // current homeroom by membership in the store, then use what
    // loadHomeroom() found.
    const personId = this.id;
    const found = this.store.peekAll<Group>('group').find((group) => {
      if (
        group.get('year') !== currentYear ||
        group.get('type') !== GroupType.HOMEROOM
      ) {
        return false;
      }
      for (const membership of group.get('learners') ?? []) {
        if (
          membership.personId === personId &&
          membership.status === MembershipStatus.CURRENT
        ) {
          return true;
        }
      }
      return false;
    });
    return found ?? this._loadedHomeroom ?? null;
  }

  // loadHomeroom resolves the learner's current-year homeroom, querying the
  // API when the store does not have it (a transfer pre-enrol's group at
  // the new school). The result is kept, so `homeroom` returns it after.
  loadHomeroom(): Promise<Group | null> {
    const inStore = this.homeroom;
    // Found in the store, not a learner, or already looked up (null when the
    // lookup found none): nothing to query.
    if (
      inStore ||
      this.type !== PersonType.LEARNER ||
      this._loadedHomeroom !== undefined
    ) {
      return Promise.resolve(inStore);
    }
    return (this._homeroomLookup ??= this.store
      .query<Group>('group', {
        // TS-STANDIN(services/session): common session.js is JS, so
        // activeSchool is any.
        schoolId: this.session.activeSchool as string,
        learners: JSON.stringify([this.id]),
        year: moment().year(),
        groupType: GroupType.HOMEROOM,
        membershipStatuses: JSON.stringify([MembershipStatus.CURRENT]),
      })
      .then((groups) => groups.at(0) ?? null)
      .catch(() => null)
      .then((group) => {
        if (!this.isDestroying && !this.isDestroyed) {
          this._loadedHomeroom = group;
        }
        return group;
      })
      .finally(() => {
        this._homeroomLookup = undefined;
      }));
  }

  @alias('learner.enrolmentStub.status')
  declare enrolmentStatus: unknown;

  // Surfaced on the person so array dependent keys can watch it: `@each`
  // cannot follow a nested path, and reloading a person mutates the existing
  // learner fragment rather than replacing it.
  @alias('learner.homeroomId')
  declare homeroomId: unknown;

  @alias('learner.currentSiblings')
  declare currentSiblings: unknown;

  @dependentKeyCompat
  @cached
  get legalNameLastFirst() {
    const first = this.fieldValue('core:firstNameLegal');
    const last = this.fieldValue('core:lastNameLegal');
    if (!isEmpty(first) && !isEmpty(last)) {
      return [last, first].join(', ').trim();
    } else if (!isEmpty(first)) {
      return first;
    } else {
      return last;
    }
  }

  @dynamicField('core:accessArrangements', DataType.STRING)
  declare accessArrangements: string;

  @dynamicField('core:studentSRN', DataType.STRING)
  declare studentSRN: string;

  @dependentKeyCompat
  @cached
  get showMedicalFlag() {
    if (this.fieldValue('core:criticalInfo')) {
      return true;
    }
    if (this.fieldValue('core:medicalCondition')) {
      return true;
    }
    if (this.fieldValue('core:allergies')) {
      return true;
    }
    if (this.fieldValue('custom:medicalNotes')) {
      return true;
    }
    return !isEmpty(this.get('learner.currentMedicalConditions'));
  }

  @computed('type', 'currentYearLevel', 'enrolmentStatus')
  get ribbonText() {
    // Don't generate any text for non-Learners.
    if (this.type !== PersonType.LEARNER) {
      return '';
    }

    // currentYearLevel is a label, a number or '' (no year level).
    const year = (this.currentYearLevel as string | number | undefined) ?? 0;
    const nonSelectYearLevels = isEmpty(this.yearLevelOptions);

    // If pre-enrolled, say so.
    const status = this.enrolmentStatus;
    if (!isEmpty(status)) {
      switch (status) {
        case EnrolmentStatus.PRE_ENROLLED:
          return `Pre-Enrol ${nonSelectYearLevels ? 'Y' : ''}${year}`;
        case EnrolmentStatus.FORMER:
          return `Former ${nonSelectYearLevels ? 'Y' : ''}${year}`;
      }
    }

    // Otherwise, always render a year level (empty === 0).
    return (nonSelectYearLevels ? 'Year ' : '') + year;
  }

  calculateFundingYearLevel(asAtDate: string) {
    // If the FYL has been overridden by the school, use the override.
    const override =
      this.fieldValue<number>('nzMOE:fundingYearLevelOverride') ?? 0;
    if (!isEmpty(override) && override > 0) {
      // Offset if as-at date is not this year
      let offset = 0;
      if (!isEmpty(asAtDate)) {
        offset = moment(asAtDate).year() - moment().year();
      }

      return override + offset;
    }

    const firstSchoolingDate = this.fieldValue<string>(
      'nzMOE:firstSchoolingDate',
    )!;
    const currentYearLevel = this.fieldValue<number>('nzMOE:currentYearLevel')!;
    const lastAttendanceDate = this.fieldValue<string>('nzMOE:lastAttendance')!;
    const dateOfBirth = this.fieldValue<string>('core:dob')!;
    const studentType = this.fieldValue('nzMOE:type');

    if (isEmpty(firstSchoolingDate) || isEmpty(dateOfBirth)) {
      // Insufficient information to calculate a funding year level.
      return 0;
    }

    // If a Roll Return Date is not passed in, calculate the Learner's FYL as at today.
    if (isEmpty(asAtDate)) {
      asAtDate = moment().format('YYYY-MM-DD');
    }

    const asAt = moment(asAtDate);
    const rollReturnYear = asAt.format('YYYY');
    const firstSchooling = moment(firstSchoolingDate);
    if (!firstSchooling.isValid()) {
      // Invalid date supplied for first schooling date.
      return 0;
    }

    const firstSchoolingYear = firstSchooling.format('YYYY');
    const julyRollReturn = asAt.clone().set({ month: 6, date: 1 });

    let fundingYearLevel =
      parseInt(rollReturnYear, 10) - parseInt(firstSchoolingYear, 10) + 1;

    /**
     * Students who start school after the compulsory starting age of 6 yrs old (e.g.
     * migrant children or home-schooled children) will be given the same Funding Year
     * Level as the majority of students the same age
     */
    // The logic for this has been bumped up from the MoE specification to stop oddities happening with year 0/1 students.
    const dob = moment(dateOfBirth);

    if (!dob.isValid()) {
      // Date of birth couldn't be parsed, so we can't calculate funding year level.
      return 0;
    }

    const startingAge = firstSchooling.diff(dob, 'years');
    if (startingAge > 6) {
      fundingYearLevel = currentYearLevel;
    } else {
      /**
       * Students who begin attending a school for the first time between 1 January and 1 July (inclusive) are coded as
       * YEAR 1.
       */
      const firstOfJan = asAt.clone().set({ month: 0, date: 1 });
      if (
        firstSchooling.isSameOrAfter(firstOfJan, 'day') &&
        firstSchooling.isSameOrBefore(julyRollReturn, 'day')
      ) {
        return 1;
      }

      /**
       * Students who begin attending school for the first time between the day after July roll count (2nd July) and
       * 31st December (inclusive) are coded as YEAR 0.  We've already processed the first half of the year, so if
       * the year matches, they fall into this category.
       * ...
       */
      if (firstSchooling.isSame(asAt, 'year')) {
        return 0;
      }

      /**
       * ... (Year 0 students are not counted in the July Roll Returns but will be present in the September Roll
       * Returns for composite schools and will be picked up in the following March Roll Returns as YEAR 1 students)
       *
       * Error: 675 - Student who started school after 1 July (t-1) must have FUNDING YEAR LEVEL 1
       */
      const previousJulyReturn = julyRollReturn.clone().subtract(1, 'years');
      if (firstSchooling.isAfter(previousJulyReturn, 'day')) {
        return 1;
      }

      // If they are in the July period always minus 1 from the current year level.
      const firstSchoolingYearJuly = firstSchooling
        .clone()
        .set({ month: 6, date: 1 });
      if (firstSchooling.isAfter(firstSchoolingYearJuly, 'day')) {
        fundingYearLevel--;
      }
    }

    /**
     * Funding Year Level for all students is increased by 1 at the start of each school
     * year and will generally remain the same value for the whole school year. The one
     * exception to this is when a student leaves school between 1 Jan and 1 March –
     * see Note below
     *
     * NOTE: Special case for students year 9 and above
     * Students, who attend for a period of time at the beginning of the school year
     * (and who have had their Funding Year Level incremented) but leave school
     * before 1 March, must have their Funding Year level decreased by 1 before the
     * school leaver data file is generated
     */
    if (currentYearLevel >= 9) {
      const firstOfMarch = asAt.clone().set({ month: 2, date: 1 });
      if (!isEmpty(lastAttendanceDate)) {
        const lastAttendance = moment(lastAttendanceDate);
        if (lastAttendance.isValid() && lastAttendance.isBefore(firstOfMarch)) {
          return Math.min(15, fundingYearLevel - 1);
        }
      }
    }

    /**
     * Students whose progress through the school system has been delayed or
     * accelerated have their Funding Year Level increased by one at the start of each
     * school year in the same way as other students.
     */
    // continue...

    /**
     * The Funding Year Level for a student can be reset as YEAR 7, YEAR 8 and
     * YEAR 9 levels only
     */
    switch (currentYearLevel) {
      case 7:
        fundingYearLevel = 7;
        break;
      case 8:
        fundingYearLevel = 8;
        break;
    }

    // When the learner's school is not in the store (for example a former
    // learner at a different school), schoolType is '' and no school type
    // rule applies.
    const schoolType =
      (this.learnerSchool()?.get('fields.nzMOE:schoolType:type.value') as
        string | undefined) ?? '';

    /**
     * To generate appropriate funding and staffing for technology students in their final
     * two years of Primary and Intermediate schooling students must have their
     * Funding Year Level set to YEAR 7 or YEAR 8
     */
    if (['20', '21', '22'].includes(schoolType) && currentYearLevel >= 7) {
      fundingYearLevel = currentYearLevel;
    }

    /**
     * 2020 Roll Return Specification Manual Page 48:
     * "Contributing (1-6) schools must have the ability to reset the Funding Year Level of ORS students
     * to YEAR 8,in order for these students to count towards their funding calculations."
     * Here we do this automatically...
     */
    const ors = (this.fieldValue<string>('nzMOE:ors') ?? '').trim();
    if (
      schoolType === '21' &&
      fundingYearLevel > 8 &&
      !['', 'N'].includes(ors)
    ) {
      fundingYearLevel = 8;
    }

    /**
     * All students in their first year of secondary schooling will be classified as YEAR 9
     */
    if (['30', '32', '35', '40', '41'].includes(schoolType)) {
      if (currentYearLevel === 9) {
        fundingYearLevel = 9;
      }
      if (['40', '41'].includes(schoolType) && fundingYearLevel < 9) {
        // Secondary schools and Teen Parent Units
        fundingYearLevel = 9;
      }
    }

    /**
     * Students aged >16 who have been absent from schooling for at least one year
     * will be given the same Funding Year Level that they had when they left
     */
    // How the hell can you tell this?? MoE: You can't.
    /**
     * Roll Returns 2019:
     *   We suggest that this section is removed from your calculation as currently it is blanket changing FYL’s for
     *   all students over 16 to their current year level which does not fit the intended parameters of this rule.
     */
    //let currentAge = dob.diff(asAt, 'years');
    //if (studentType === 'RE' && currentAge > 16) {
    //  fundingYearLevel = currentYearLevel;
    //}

    /**
     * Returning adult students =>19 years of age on 1 January who returned to school
     * will assume the Funding Year Level corresponding to the level at which the
     * majority of their subjects are being taken
     */
    const currentAge = dob.diff(asAt, 'years');
    if ((studentType === 'AD' || studentType === 'RA') && currentAge >= 19) {
      // TODO: Implement instructional year level calculation
      // fundingYearLevel = calculateModalValueInstructionalYearLevel($mappedData);
      // If the student has no subjects, Funding Year Level will come back as null. This most likely means the
      // student has left the school. Therefore, we'll set the funding year level to the student's Current Year Level
      // as the Roll Return process will strip them from the final outcome...
      // if (is_null($fundingYearLevel)) {
      fundingYearLevel = currentYearLevel;
      // }
    }

    return Math.min(15, fundingYearLevel);
  }

  @dependentKeyCompat
  @cached
  get fundingYearLevel() {
    return this.calculateFundingYearLevel(moment().format('YYYY-MM-DD'));
  }

  @dependentKeyCompat
  @cached
  get primaryEthnicity() {
    return this.ethnicities[0];
  }

  // rankedL1Ethnicity calculates the ranked Level 1 ethnicity
  @dependentKeyCompat
  @cached
  get rankedL1Ethnicity() {
    const rankedCodes = [
      { code: '211', label: 'Māori' },
      { code: '351', label: 'Pasifika' },
      { code: '361', label: 'Pasifika' },
      { code: '341', label: 'Pasifika' },
      { code: '331', label: 'Pasifika' },
      { code: '321', label: 'Pasifika' },
      { code: '311', label: 'Pasifika' },
      { code: '371', label: 'Pasifika' },
      { code: '411', label: 'Asian' },
      { code: '412', label: 'Asian' },
      { code: '413', label: 'Asian' },
      { code: '414', label: 'Asian' },
      { code: '431', label: 'Asian' },
      { code: '421', label: 'Asian' },
      { code: '441', label: 'Asian' },
      { code: '442', label: 'Asian' },
      { code: '443', label: 'Asian' },
      { code: '444', label: 'Asian' },
      { code: '511', label: 'MELAA' },
      { code: '521', label: 'MELAA' },
      { code: '531', label: 'MELAA' },
      { code: '611', label: 'Other' },
      { code: '999', label: 'Other' },
      { code: '128', label: 'European / Pākehā' },
      { code: '121', label: 'European / Pākehā' },
      { code: '127', label: 'European / Pākehā' },
      { code: '122', label: 'European / Pākehā' },
      { code: '123', label: 'European / Pākehā' },
      { code: '124', label: 'European / Pākehā' },
      { code: '125', label: 'European / Pākehā' },
      { code: '126', label: 'European / Pākehā' },
      { code: '129', label: 'European / Pākehā' },
      { code: '111', label: 'European / Pākehā' },
    ];

    return this.getRankedEthnicity(rankedCodes);
  }

  // displayEthnicity calculates the ranked Level 2 ethnicity
  @dependentKeyCompat
  @cached
  get displayEthnicity() {
    const rankedCodes = [
      { code: '211', label: 'Māori' },
      { code: '351', label: 'Tokelauan' },
      { code: '361', label: 'Fijian' },
      { code: '341', label: 'Niuean' },
      { code: '331', label: 'Tongan' },
      { code: '321', label: 'Cook Islands Māori' },
      { code: '311', label: 'Samoan' },
      { code: '371', label: 'Other Pacific' },
      { code: '411', label: 'Southeast Asian' },
      { code: '412', label: 'Southeast Asian' },
      { code: '413', label: 'Southeast Asian' },
      { code: '414', label: 'Southeast Asian' },
      { code: '431', label: 'Indian' },
      { code: '421', label: 'Chinese' },
      { code: '441', label: 'Other Asian' },
      { code: '442', label: 'Other Asian' },
      { code: '443', label: 'Other Asian' },
      { code: '444', label: 'Other Asian' },
      { code: '511', label: 'Middle Eastern' },
      { code: '521', label: 'Latin American' },
      { code: '531', label: 'African' },
      { code: '611', label: 'Other' },
      { code: '128', label: 'Other European' },
      { code: '121', label: 'Other European' },
      { code: '127', label: 'Other European' },
      { code: '122', label: 'Other European' },
      { code: '123', label: 'Other European' },
      { code: '124', label: 'Other European' },
      { code: '125', label: 'Other European' },
      { code: '126', label: 'Other European' },
      { code: '129', label: 'Other European' },
      { code: '111', label: 'New Zealand' },
      { code: '999', label: 'Not Stated' },
    ];

    return this.getRankedEthnicity(rankedCodes);
  }

  // rankedL3Ethnicity calculates the ranked Level 3 ethnicity
  @dependentKeyCompat
  @cached
  get rankedL3Ethnicity() {
    const rankedCodes = [
      { code: '211', label: 'Māori' },
      { code: '351', label: 'Tokelauan' },
      { code: '361', label: 'Fijian' },
      { code: '341', label: 'Niuean' },
      { code: '331', label: 'Tongan' },
      { code: '321', label: 'Cook Islands Māori' },
      { code: '311', label: 'Samoan' },
      { code: '371', label: 'Other Pacific Peoples' },
      { code: '411', label: 'Filipino' },
      { code: '412', label: 'Cambodian' },
      { code: '413', label: 'Vietnamese' },
      { code: '414', label: 'Other Southeast Asian' },
      { code: '431', label: 'Indian' },
      { code: '421', label: 'Chinese' },
      { code: '441', label: 'Sri Lankan' },
      { code: '442', label: 'Japanese' },
      { code: '443', label: 'Korean' },
      { code: '444', label: 'Other Asian' },
      { code: '511', label: 'Middle Eastern' },
      { code: '521', label: 'Latin American' },
      { code: '531', label: 'African' },
      { code: '611', label: 'Other Ethnicity' },
      { code: '999', label: 'Not Stated' },
      { code: '128', label: 'Australian' },
      { code: '121', label: 'British/Irish' },
      { code: '127', label: 'German' },
      { code: '122', label: 'Dutch' },
      { code: '123', label: 'Greek' },
      { code: '124', label: 'Polish' },
      { code: '125', label: 'South Slav' },
      { code: '126', label: 'Italian' },
      { code: '129', label: 'Other European' },
      { code: '111', label: 'NZ European/Pākehā' },
    ];

    return this.getRankedEthnicity(rankedCodes);
  }

  // ethnicities is the nzMOE:ethnicity codes.
  get ethnicities(): readonly string[] {
    return (this.fieldValue('nzMOE:ethnicity') as string[] | undefined) ?? [];
  }

  getRankedEthnicity(codes: { code: string; label: string }[]) {
    let ranked = 'Other';
    const ethnicities = this.ethnicities;
    for (let i = 0; i < codes.length; i++) {
      if (ethnicities.includes(codes[i]!.code)) {
        ranked = codes[i]!.label;
        break;
      }
    }

    return ranked;
  }

  @dynamicField('nzMOE:ethnicity', DataType.LIST)
  declare allEthnicities: string[];

  @dependentKeyCompat
  @cached
  get atsi() {
    const atsi = this.fieldValue('core:atsi');
    const match = Atsi.values.find((i) => i.value === atsi);
    return match ? match.label : 'Unspecified';
  }

  @computed('session.school.jurisdiction')
  get jurisdictionalEthnicity() {
    // TS-STANDIN(services/session): common session.js is JS, so school types
    // as null.
    switch ((this.session.school as unknown as School).jurisdiction) {
      case Jurisdiction.NZ_MOE:
        return this.rankedL3Ethnicity;
      case Jurisdiction.AU_NSW:
        return this.atsi;
      default:
        return;
    }
  }

  @dependentKeyCompat
  @cached
  get returnLanguageCode() {
    // TODO: I can't find this key anywhere in schema and it's only used by linced-app/app/controllers/admin/lists/exports/generate-upload.js
    const lang = this.fieldValue('nzMOE:firstLanguage');

    if (isEmpty(lang)) {
      return '';
    } else if (lang === 'ENG' || lang === 'English') {
      return 1;
    } else {
      return 2;
    }
  }

  @computed('type', 'learner.enrolmentId')
  get enrolment() {
    // Don't try to return an Enrolment for non-Learners.
    if (this.type !== PersonType.LEARNER) {
      return null;
    }
    // The common person types learner as the common learner fragment.
    if (isEmpty((this.learner as Learner).enrolmentId)) {
      return null;
    }

    // If we already have the relevant Enrolment record, use peekRecord to return it immediately.
    const enrolment = this.store.peekRecord(
      'people/enrolment',
      (this.learner as Learner).enrolmentId,
    );
    if (!isEmpty(enrolment)) {
      return enrolment;
    }

    // We don't have the record already - fetch it.
    return this.store.findRecord(
      'people/enrolment',
      (this.learner as Learner).enrolmentId,
    );
  }

  @dependentKeyCompat
  @cached
  get address() {
    const keys = ['address1', 'address2', 'address3', 'address4', 'postcode'];
    let prefix = 'core';
    if (this.get('session.school.jurisdiction') === Jurisdiction.NZ_MOE) {
      prefix = 'nzMOE';
    }
    return arrays.compact(
      keys.map((key) => {
        const value = this.fieldValue<string>(`${prefix}:${key}`);
        if (isEmpty(value)) {
          // compact only removes null and undefined values
          return null;
        }
        return value;
      }),
    );
  }

  /**
   * ageOnDate, monthsAtSchoolOnDate, weeksAtSchoolOnDate & birthday are used for List Builder.
   */
  ageOnDate(date: string, sortable = false) {
    if (this.dob) {
      const duration = moment.duration(moment(date).diff(this.dob));
      // The moment types do not declare the private _milliseconds field.
      if (
        (duration as unknown as { _milliseconds: number })._milliseconds < 0
      ) {
        return;
      }

      if (sortable) {
        return (duration as unknown as { _milliseconds: number })._milliseconds;
      }

      // moment.duration does a terrible job of calculating any duration >= 1 year due to leap years
      // So we have to do this dance to get an accurate year/month/day age
      // https://stackoverflow.com/a/50324686
      const start = moment(this.dob);
      const end = moment(date);

      const years = end.diff(start, 'year');
      start.add(years, 'years');
      const months = end.diff(start, 'months');
      start.add(months, 'months');
      const days = end.diff(start, 'days');

      const yearsString = years ? `${years} Year${years !== 1 ? 's' : ''}` : '';
      const monthsString = months
        ? `${years ? ', ' : ''}${months} Month${months !== 1 ? 's' : ''}`
        : '';
      const daysString = days
        ? `${years || months ? ' & ' : ''}${days} Day${days !== 1 ? 's' : ''}`
        : '';

      return `${yearsString}${monthsString}${daysString}`;
    }
  }

  birthday(_: string, sortable = false) {
    if (this.dob) {
      if (sortable) {
        // For sorting, calculate the birthday relative to today's date.
        // This way the sort illustrates either the next birthday first
        // or the furthest away birthday first.
        // Known leap years are used for comparisons to avoid issues with any
        // students whose birthday falls on the 29th of February.
        const yearA = 2008;
        const yearB = 2012;
        const today = moment().year(yearA);
        const relativeBirthdayMonthDay = moment(this.dob).format('MM-DD');
        let relativeBirthday = moment(`${yearA}-${relativeBirthdayMonthDay}`);
        let diff = relativeBirthday.diff(today);
        if (diff < 0) {
          relativeBirthday = moment(`${yearB}-${relativeBirthdayMonthDay}`);
          diff = relativeBirthday.diff(today);
        }
        return diff;
      }

      return moment(this.dob).format('Do MMMM');
    }
  }

  /**
   * genderClass returns a CSS class based on the user's set gender.
   */
  @computed('preferredGender', 'type')
  get genderClass() {
    if (this.type === PersonType.STAFF) {
      return '';
    }
    switch (this.preferredGender) {
      case 'M':
        return 'gender-male';
      case 'F':
        return 'gender-female';
    }
    return 'gender-neutral';
  }

  @computed('learner.schoolId')
  get schoolName() {
    return this.learnerSchool()?.name;
  }

  // learnerSchool returns the learner's school, or null when the learner has
  // no school or the school is not in the store. peekRecord throws on an
  // empty id. peekRecord('school', ...) finds the record, but the school
  // model re-exports schools/school, so TS knows only 'schools/school'.
  // The cast is for TS.
  learnerSchool() {
    const schoolId = this.get('learner.schoolId') as string | undefined;
    return schoolId
      ? this.store.peekRecord<School>('school' as 'schools/school', schoolId)
      : null;
  }

  // isAdmin returns a boolean
  // Primarily for ListBuilder but you can use it for whatever you want
  @computed('userId', 'type')
  get isAdmin() {
    if (this.type !== PersonType.STAFF) {
      return false;
    }
    const user = this.store.peekRecord<User>('user', this.userId);
    if (!user) {
      return false;
    }
    const scopes = user.get('scopes');
    return scopes && scopes.includes(Scopes.TENANT_ADMIN);
  }

  // hasHelpdeskAccess a boolean
  // Primarily for ListBuilder but you can use it for whatever you want
  @computed('session.school.staffWithSupportAccess[]', 'type')
  get hasHelpdeskAccess() {
    if (this.type !== PersonType.STAFF) {
      return false;
    }
    // TS-STANDIN(services/session): common session.js is JS, so school types
    // as null.
    const staffWithSupportAccess = (
      this.session.school as unknown as School
    ).get('staffWithSupportAccess');
    return !!arrays
      .resolved(staffWithSupportAccess)
      .find((staff) => staff.id === this.id);
  }

  // pageAccessLevelLabel returns the label for this staff member's current page access level
  // Primarily for ListBuilder but you can use it for whatever you want
  @computed('pageAccessLevel', 'type')
  get pageAccessLevelLabel() {
    if (this.type !== PersonType.STAFF) {
      return '';
    }
    return StaffPageAccess.getLabel(this.pageAccessLevel) || '';
  }

  // restrictedFieldLabels returns a comma separated list of the labels
  // for the restricted fields that this staff member can access
  // Primarily for ListBuilder but you can use it for whatever you want
  @computed('roles', 'type')
  get restrictedFieldLabels() {
    if (this.type !== PersonType.STAFF) {
      return '';
    }
    return arrays
      .compact(
        this.roles.map((role) => {
          return FieldAccess.getLabel(role);
        }),
      )
      .join(', ');
  }

  // roleLabels returns a comma separated list of the labels for this staff member's current roles
  // Primarily for ListBuilder but you can use it for whatever you want
  @computed('roles', 'type')
  get staffRoleLabels() {
    return arrays
      .compact(
        this.roles.map((role) => {
          return Staff.getLabel(role);
        }),
      )
      .join(', ');
  }

  // isHeroSource returns True if staff was created in hero. False if it was imported.
  @dependentKeyCompat
  @cached
  get isHeroSource() {
    if (this.type !== PersonType.STAFF) {
      return true;
    }
    if (this.jurisdiction !== Jurisdiction.AU_NSW) {
      return true;
    }
    const staffOriginSAIS = this.fieldValue('core:originSais');
    if (isEmpty(staffOriginSAIS)) {
      return true;
    }
    return staffOriginSAIS !== true;
  }

  @computed('firstInitial')
  get firstInitialBase() {
    return stripDiacritics(this.firstInitial);
  }

  @computed('lastInitial')
  get lastInitialBase() {
    return stripDiacritics(this.lastInitial);
  }

  // Various Medical fields lifted from the medical-room report for use in Event Reports
  @computed('learner.medicalConditions.@each.isCurrent')
  get allCurrentConditions() {
    let conditions: FragmentArray<MedicalCondition> | MedicalCondition[] =
      this.get('learner.medicalConditions') as FragmentArray<MedicalCondition>;
    if (isEmpty(conditions)) {
      return [];
    }
    conditions = arrays.filterBy(
      arrays.filterBy(conditions, 'isCurrent'),
      'userCanAccess',
    );
    return conditions.sort(arrays.byKeys('priority'));
  }

  @filterBy('allCurrentConditions', 'isSerious')
  declare seriousCurrentConditions: unknown[];

  @computed('allCurrentConditions.@each.{type,isSerious}')
  get nonSeriousCurrentAllergies() {
    return this.allCurrentConditions.filter((condition) => {
      return (
        condition.type === MedicalConditionType.NON_SERIOUS_ALLERGY &&
        !condition.isSerious
      );
    });
  }

  @computed('allCurrentConditions.@each.{type,isSerious}')
  get nonSeriousCurrentConditions() {
    return this.allCurrentConditions.filter((condition) => {
      return (
        (condition.type ===
          MedicalConditionType.NON_SERIOUS_MEDICAL_CONDITION ||
          condition.type ===
            MedicalConditionType.SHORT_TERM_MEDICAL_CONDITION) &&
        !condition.isSerious
      );
    });
  }

  @dynamicField('core:criticalInfo', DataType.STRING)
  declare legacyCritical: string;

  @dynamicField('core:allergies', DataType.STRING)
  declare legacyAllergy: string;

  @dynamicField('core:medicalCondition', DataType.STRING)
  declare legacyMedicalCondition: string;

  @dynamicField('core:medicalTreatment', DataType.STRING)
  declare legacyMedicalTreatment: string;

  @dynamicField('core:doctor', DataType.STRING)
  declare legacyDoctor: string;

  @alias('learner.currentMedicalContacts')
  declare medicalContacts: unknown;

  @computed('planUploaded')
  get planDisplay() {
    return this.planUploaded ? 'Yes' : '';
  }

  @dependentKeyCompat
  @cached
  get planUploaded() {
    if (this.fieldValue('core:aSCIAPlan') === 'Yes') {
      return true;
    }
    if (isEmpty(this.allCurrentConditions)) {
      return false;
    }
    return this.allCurrentConditions.some((condition) => {
      return (
        (condition.get('fields.core:actionPlans.valueList.length') as number) >
        0
      );
    });
  }

  @hasMany('medical/medication', { async: true, inverse: 'personId' })
  declare medications: AsyncHasMany<Medication>;

  @computed(
    'id',
    'medications.[]',
    'medications.@each.{deleted,medicalCondition}',
  )
  get medicationsWithoutConditions() {
    const medications = this.medications;
    if (isEmpty(medications)) {
      return [];
    }
    return arrays.resolved(medications).filter((medication) => {
      return (
        (isEmpty(medication.medicalCondition) ||
          medication.medicalCondition === undefined) &&
        medication.deleted === false
      );
    });
  }

  @computed(
    'allCurrentConditions.[]',
    'legacyCritical',
    'legacyAllergy',
    'legacyMedicalCondition',
    'legacyMedicalTreatment',
    'planUploaded',
    'medicationsWithoutConditions.[]',
  )
  get hasMedical() {
    return (
      this.allCurrentConditions.length ||
      this.legacyCritical ||
      this.legacyAllergy ||
      this.legacyMedicalCondition ||
      this.legacyMedicalTreatment ||
      this.planUploaded ||
      this.medicationsWithoutConditions.length
    );
  }
}
